<script setup lang="ts">
  import { CartLayout, CartWithSummaryLayout } from '@storefront/components';
  import { useAuthenticated, useCountriesManager, useHttpClient, useRoute } from '@othercommerce/foundation';
  import { useVerifiedCart } from '@storefront/storefront';
  import { computed, ref } from 'vue';
  import { type Dictionary, CustomerType } from '@othercommerce/domain';

  defineOptions({
    layout: [CartLayout, CartWithSummaryLayout],
    inheritAttrs: false,
  });

  interface Props {
    isMedicalProvider: boolean;
    regions: Record<string, Dictionary>,
  }

  const props = defineProps<Props>();

  const authenticated = useAuthenticated();
  const cart = useVerifiedCart();
  const countriesManager = useCountriesManager();
  const http = useHttpClient();
  const route = useRoute();

  const savingAddress = ref(false);

  const shippingData = computed(() => {
    return {
      shippingFirstName: cart.value.shippingFirstName,
      shippingLastName: cart.value.shippingLastName,
      shippingCompany: cart.value.shippingCompany,
      shippingStreet: cart.value.shippingStreet,
      shippingBuildingNr: cart.value.shippingBuildingNr,
      shippingApartmentNr: cart.value.shippingApartmentNr,
      shippingFirstLine: cart.value.shippingFirstLine,
      shippingSecondLine: cart.value.shippingSecondLine,
      shippingPostcode: cart.value.shippingPostcode,
      shippingCity: cart.value.shippingCity,
      shippingRegion: cart.value.shippingRegion,
      shippingEmail: cart.value.shippingEmail,
      shippingPhone: cart.value.shippingPhone,
    };
  });

  const countryRegions = computed(() => {
    if (!cart.value.country.code || !props.regions || props.isMedicalProvider) return null;

    return props.regions[cart.value.country.code];
  });

  const implicit = computed(() => {
    return countriesManager.isImplicit(cart.value.country.code);
  });

  const isCompanyNameVisible = computed(() => {
    if (authenticated.value) {
      return authenticated.value.type !== CustomerType.PERSONAL;
    } else {
      return true;
    }
  });

  const isAccountPersonal = computed(() => {
    if (authenticated.value) {
      return authenticated.value.type === CustomerType.PERSONAL;
    } else {
      return false;
    }
  });

  function saveAddress(data: any) {
    savingAddress.value = true;

    http.post(route('web.account.address.delivery.importFromCart'), data).finally(() => {
      savingAddress.value = false;
    });
  }
</script>
<template>
  <FormController :data="shippingData" :action="$route('web.cart.shipping.address.update')" method="PATCH" v-slot="{ data, processing, submit }">
    <div class="shipping-address">
      <div class="shipping-address-container">
        <UiButtonGroup v-if="authenticated">
          <SfButton primary small :disabled="processing || savingAddress" :href="$route('web.cart.shipping.address.choose')">
            <template #before>
              <SfIconBook aria-hidden="true" />
            </template>
            <template #default>
              {{ $t('SHOP.CART.ACTION.FILL_FROM_SAVED_ADDRESSES') }}
            </template>
          </SfButton>
          <SfButton v-if="!isMedicalProvider" small :disabled="processing || savingAddress" @click="saveAddress(data)">
            <template #before>
              <SfIconPlus aria-hidden="true" />
            </template>
            <template #default>
              {{ $t('SHOP.CART.ACTION.SAVE_IN_SHIPPING_ADDRESSES') }}
            </template>
          </SfButton>
        </UiButtonGroup>
        <div class="shipping-address-form">
          <SfFormInput type="text" :label="$t('SHOP.CART.LABEL.FIRSTNAME')" name="shippingFirstName" required />
          <SfFormInput type="text" :label="$t('SHOP.CART.LABEL.LASTNAME')" name="shippingLastName" required />

          <template v-if="isCompanyNameVisible">
            <SfFormInput type="text" :label="$t('SHOP.CART.LABEL.COMPANY_NAME')" name="shippingCompany" :required="isAccountPersonal" />
          </template>

          <template v-if="implicit">
            <div class="address-line">
              <div class="address-street">
                <SfFormInput type="text" :label="$t('SHOP.CART.LABEL.STREET')" name="shippingStreet" required />
              </div>
              <div class="address-building-nr">
                <SfFormInput type="text" :label="$t('SHOP.CART.LABEL.BUILDING_NR')" name="shippingBuildingNr" required />
              </div>
              <div class="address-apartment-nr">
                <SfFormInput type="text" :label="$t('SHOP.CART.LABEL.APARTMENT_NR')" name="shippingApartmentNr" />
              </div>
            </div>
          </template>

          <template v-else>
            <SfFormInput type="text" :label="$t('SHOP.CART.LABEL.FIRST_LINE')" name="shippingFirstLine" required />
            <SfFormInput type="text" :label="$t('SHOP.CART.LABEL.SECOND_LINE')" name="shippingSecondLine" />
          </template>

          <div class="address-line">
            <div class="address-postcode">
              <SfFormInput type="text" :label="$t('SHOP.CART.LABEL.POSTCODE')" name="shippingPostcode" required />
            </div>
            <div class="address-city">
              <SfFormInput type="text" :label="$t('SHOP.CART.LABEL.CITY')" name="shippingCity" required />
            </div>
          </div>

          <SfFormSelect v-if="countryRegions" :options="countryRegions" :label="$t('SHOP.CART.LABEL.REGION')" name="shippingRegion" />
          <SfFormInput v-else type="text" :label="$t('SHOP.CART.LABEL.REGION')" name="shippingRegion" />

          <SfFormInput type="email" :label="$t('SHOP.CART.LABEL.SHIPPING_EMAIL')" name="shippingEmail" required />
          <SfFormInput type="tel" :label="$t('SHOP.CART.LABEL.SHIPPING_PHONE')" name="shippingPhone" required />
        </div>
        <UiButtonGroup>
          <SfButton primary :loading="processing || savingAddress" @click="submit">
            {{ $t('SHOP.CART.ACTION.CONTINUE') }}
          </SfButton>
        </UiButtonGroup>
      </div>
    </div>
  </FormController>
  <!-- @TODO kurde faja bez tego widok z modalem dla adresów nie działa -->
  <RouterView />
</template>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .shipping-address {
    .shipping-address-container {
      display: flex;
      flex-direction: column;
      gap: 2rem;
    }

    .shipping-address-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
    }

    .shipping-address-form {
      display: flex;
      flex-direction: column;
      max-width: 672px;
      gap: var(--form-gap-y) var(--form-gap-x);

      .address-line {
        display: flex;
        flex-direction: row;
        gap: var(--form-gap-y) var(--form-gap-x);
      }

      .address-street {
        flex: 1 1 auto;
      }

      .address-building-nr,
      .address-apartment-nr {
        flex: 0 0 128px;
      }

      .address-postcode {
        flex: 0 0 208px;
      }

      .address-city {
        flex: 1 1 auto;
      }


      @include media.query(sm) {
        .address-line {
          flex-wrap: wrap;
        }
        .address-postcode,
        .address-apartment-nr,
        .address-building-nr,
        .address-street {
          flex-basis: 100%;
        }
      }
    }
  }
</style>
